import React, { useEffect, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import DateFnsUtils from '@date-io/date-fns';
import {
    MuiPickersUtilsProvider,
    KeyboardTimePicker,
    KeyboardTimePickerProps,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

interface FormikTimePickerProps extends Omit<KeyboardTimePickerProps, 'value' | 'onChange'> {
    name: string;
    className?: string;
    style?: React.CSSProperties;
    showErrorBelow?: boolean;
    clampToRange?: { from: number | null; to: number | null };
    onInvalidInputChange?: (isInvalid: boolean) => void;
}

function numberToDate(value: number | null | undefined): Date | null {
    if (value === null || value === undefined) return null;
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 100);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
}

function dateToNumber(date: MaterialUiPickersDate): number | null {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    return date.getHours() + date.getMinutes() / 100;
}

const FormikTimePicker: React.FC<FormikTimePickerProps> = ({
    name,
    className,
    style,
    required,
    inputVariant = 'filled',
    showErrorBelow,
    clampToRange,
    onError,
    onInvalidInputChange,
    ...otherProps
}) => {
    const [field, meta] = useField<number | null>({
        name,
        validate: (value: number | null) => {
            if (required && (value === null || value === undefined)) {
                return 'Required';
            }
            return undefined;
        },
    });
    const { setFieldValue, submitCount } = useFormikContext();
    const latestValue = useRef<number | null>(field.value);
    const [hasInvalidInput, setHasInvalidInput] = useState(false);
    const [hasPickerValidationError, setHasPickerValidationError] = useState(false);

    useEffect(() => {
        latestValue.current = field.value;
    }, [field.value]);

    const showError = Boolean((meta.touched || submitCount > 0) && meta.error);

    const clampValue = (value: number | null) =>
        value === null ||
        !clampToRange ||
        clampToRange.from === null ||
        clampToRange.to === null ||
        (clampToRange.from === 0 && clampToRange.to === 0)
            ? value
            : Math.max(clampToRange.from, Math.min(clampToRange.to, value));

    const handleDateChange = (date: MaterialUiPickersDate, inputValue?: string | null) => {
        const valueFromDate = dateToNumber(date);
        const isInvalidInput =
            inputValue != null && inputValue.trim() !== '' && valueFromDate === null;
        setHasInvalidInput(isInvalidInput);
        onInvalidInputChange?.(isInvalidInput);
        const value = clampValue(valueFromDate);
        latestValue.current = value;
        setFieldValue(name, value);
    };

    const handlePickerError: NonNullable<KeyboardTimePickerProps['onError']> = (error, value) => {
        setHasPickerValidationError(Boolean(error));
        onError?.(error, value);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setFieldValue(name, clampValue(latestValue.current), false);
        field.onBlur(event);
    };

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardTimePicker
                {...otherProps}
                required={required}
                inputVariant={inputVariant}
                ampm={false}
                format="HH:mm"
                mask="__:__"
                placeholder="15:30"
                value={numberToDate(field.value)}
                onChange={handleDateChange}
                onError={handlePickerError}
                onBlur={handleBlur}
                error={showError || hasInvalidInput || hasPickerValidationError}
                helperText={
                    showError
                        ? meta.error
                        : hasInvalidInput || hasPickerValidationError
                        ? otherProps.invalidDateMessage || 'Enter complete time (HH:mm)'
                        : otherProps.helperText
                }
                FormHelperTextProps={
                    showErrorBelow
                        ? {
                              style: {
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  fontSize: 10,
                                  marginLeft: 0,
                                  ...otherProps.FormHelperTextProps?.style,
                              },
                          }
                        : otherProps.FormHelperTextProps
                }
                invalidDateMessage={
                    otherProps.invalidDateMessage || 'Enter complete time (HH:mm)'
                }
                KeyboardButtonProps={{
                    style: { display: 'none' },
                }}
                InputProps={{
                    onClick: (e) => e.preventDefault(),
                }}
                className={className}
                style={{
                    width: '100px',
                    minWidth: '90px',
                    ...style,
                }}
            />
        </MuiPickersUtilsProvider>
    );
};

export default FormikTimePicker;
